import CloseIcon from '@mui/icons-material/Close';
import type { Client } from '@src/clients/domain/Client';
import { Button } from '@src/common/components/button/Button';
import { Divider } from '@src/common/components/Divider';
import { PageContent } from '@src/common/components/PageContent';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { hasCategory } from '@src/orders/domain/CheckedFields';
import type { Order } from '@src/orders/domain/Order';
import type { TabKey } from '@src/orders/EditOrderTabs';
import { EditOrderTabs } from '@src/orders/EditOrderTabs';
import { useNavigateToOrderDetails } from '@src/orders/hooks/useNavigateToOrderDetails';
import type { EditOrderFormInputs } from '@src/orders/schemas/EditOrderFormInputs';
import { TabPanel } from '@src/orders/TabPanel';
import { tabsPanels } from '@src/orders/TabsPanels';
import { OrdersNavigation } from '@src/routing/OrdersNavigation';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export const ViewOrder = ({
  order,
  client,
}: {
  order: Order;
  client: Client;
}) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const availableTabs = tabsPanels.filter((tabPanel) =>
    hasCategory(
      tabPanel.category,
      order.getFields().fields,
      order.getPermitsInstructions(),
    ),
  );

  const onChangeSection = (tabKey: TabKey) => {
    if (tabKey === 'permits') {
      return navigate(OrdersNavigation.addPermits.url(order.id));
    }
    const tabIndex = availableTabs.findIndex(
      (panel) => panel.category === tabKey,
    );
    setCurrentTab(tabIndex);
  };

  const readonly = true as const;
  const defaultFormValues: EditOrderFormInputs = order.getMostRecentEntries(
    client,
  ) as EditOrderFormInputs;
  const methods = useForm<EditOrderFormInputs>({
    defaultValues: defaultFormValues,
    disabled: true,
    context: {
      mode: 'completeApplication',
    },
  });

  return (
    <FormProvider {...methods}>
      <Stack direction="row" gap="16px">
        <Stack
          direction="column"
          style={{
            minWidth: 'fit-content',
            padding: '24px',
            paddingTop: '40px',
            alignItems: 'flex-start',
            backgroundColor: theme.palette.surface.surface,
          }}
        >
          <Typography className={titlePadding} component="h4" variant="h5">
            {t('orderTitle')}
          </Typography>
          <Typography className={titlePadding} variant="subtitle2">
            {order.displayId}
          </Typography>
          <EditOrderTabs
            canAddPermits={false}
            currentTab={availableTabs[currentTab]?.category}
            onChangeSection={onChangeSection}
            permitInstructions={order.getPermitsInstructions()}
            checkedFields={order.getFields().fields}
          />
        </Stack>

        <PageContent>
          <Stack
            direction="column"
            justifyContent="flex-start"
            height="100%"
            alignItems="stretch"
            gap={theme.spacing['3']}
          >
            {tabsPanels
              .filter((tabPanel) =>
                hasCategory(
                  tabPanel.category,
                  order.getFields().fields,
                  order.getPermitsInstructions(),
                ),
              )
              .map((tabPanel, index) => (
                <TabPanel key={index} index={index} value={currentTab}>
                  {tabPanel.render(
                    order,
                    order.getPermitsInstructions(),
                    order.getFields().fields,
                    client,
                    readonly,
                    methods,
                    t,
                  )}
                </TabPanel>
              ))}
            <CloseViewButton orderId={order.id} />
          </Stack>
        </PageContent>
      </Stack>
    </FormProvider>
  );
};
const CloseViewButton = ({ orderId }: { orderId: string }) => {
  const { t } = useTranslation();
  const { navigateToOrderDetails } = useNavigateToOrderDetails();

  const closeForm = () => {
    navigateToOrderDetails({ orderId });
  };

  return (
    <Stack
      zIndex={10}
      position="sticky"
      bottom="0"
      style={{ backgroundColor: theme.palette.surface.surface }}
      paddingBottom={theme.spacing['6']}
      marginX="40px"
    >
      <Divider style={{ marginBottom: theme.spacing['6'] }} />
      <Stack direction="row" justifyContent="space-between">
        <Button
          type="button"
          onClick={closeForm}
          variant="text"
          startIcon={<CloseIcon />}
        >
          {t('close')}
        </Button>
      </Stack>
    </Stack>
  );
};
const titlePadding = css({
  paddingLeft: '12px',
  fontWeight: 500,
});
